import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { puedeAccederFichaIngreso } from '../../services/permisoService';
import { obtenerUsuario } from '../../services/authService';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Autocomplete,
    TextField,
    Typography,
    Stack,
    Divider,
    Paper,
    Alert
} from '@mui/material';
import {
    Print as IconPrint,
    PictureAsPdf as IconPdf,
    Description as IconWord,
    ArrowBack as IconVolver,
    AssignmentInd as IconFicha
} from '@mui/icons-material';
import { getsParticipantesResumenView, getFichaIngreso } from '../../services/participanteService';
import { useFiltroFilial } from '../../hooks/useFiltroFilial';
import FiltroFilial from '../../components/common/FiltroFilial';
import { registrarEjecucionReporte } from '../../services/bitacoraReporteService';

const formatearFecha = (fechaStr) => {
    if (!fechaStr) return '';
    try {
        const d = new Date(fechaStr);
        if (isNaN(d.getTime())) return '';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const anio = d.getFullYear();
        return `${dia}/${mes}/${anio}`;
    } catch {
        return '';
    }
};

const FichaParticipante = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const idParam = parseInt(searchParams.get('idParticipante') || '0', 10);

    const rolesUsuario = useSelector((state) => state.seguridad?.roles) || [];
    const usuarioActual = obtenerUsuario();
    const tienePermiso = puedeAccederFichaIngreso(usuarioActual, rolesUsuario);

    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const [participantes, setParticipantes] = useState([]);
    const [participanteSeleccionado, setParticipanteSeleccionado] = useState(null);
    const [ficha, setFicha] = useState(null);
    const [cargandoLista, setCargandoLista] = useState(false);
    const [cargandoFicha, setCargandoFicha] = useState(false);
    const [error, setError] = useState(null);

    // Cargar listado de participantes para el autocompletado
    const fetchParticipantes = useCallback(async () => {
        setCargandoLista(true);
        try {
            const data = await getsParticipantesResumenView(true, false, idFilial);
            setParticipantes(data || []);
        } catch (err) {
            console.error('Error cargando participantes:', err);
        } finally {
            setCargandoLista(false);
        }
    }, [idFilial]);

    useEffect(() => {
        fetchParticipantes();
    }, [fetchParticipantes]);

    // Cargar ficha cuando cambia el id
    const cargarFicha = useCallback(async (id) => {
        if (!id || id <= 0) {
            setFicha(null);
            return;
        }
        setCargandoFicha(true);
        setError(null);
        const t0 = performance.now();
        try {
            const data = await getFichaIngreso(id);
            setFicha(data);
            registrarEjecucionReporte({
                reportPath: '/reporte/ficha-participante',
                format: 'HTML',
                reportAction: 'Render',
                timeRendering: Math.round(performance.now() - t0),
                rowCount: 1,
                status: 'rsSuccess'
            });
        } catch (err) {
            console.error('Error cargando ficha:', err);
            setError('No se pudo cargar la información de la ficha de ingreso.');
        } finally {
            setCargandoFicha(false);
        }
    }, []);

    // Sincronizar selección con idParam
    useEffect(() => {
        if (idParam > 0) {
            cargarFicha(idParam);
        }
    }, [idParam, cargarFicha]);

    // Si ya cargó la lista y hay idParam, actualizar el Autocomplete
    useEffect(() => {
        if (idParam > 0 && participantes.length > 0) {
            const encontrado = participantes.find((p) => p.id === idParam);
            if (encontrado) {
                setParticipanteSeleccionado(encontrado);
            }
        }
    }, [idParam, participantes]);

    const handleSelectParticipante = (event, valor) => {
        setParticipanteSeleccionado(valor);
        if (valor && valor.id > 0) {
            setSearchParams({ idParticipante: valor.id });
        } else {
            setSearchParams({});
            setFicha(null);
        }
    };

    const handleImprimir = () => {
        const tituloOriginal = document.title;
        const nombrePart = ficha?.participanteNombres ? `_${ficha.participanteNombres.replace(/\s+/g, '_')}` : '';
        document.title = `Ficha_Matricula${nombrePart}`;
        window.print();
        setTimeout(() => {
            document.title = tituloOriginal;
        }, 1500);
    };

    const handleExportarWord = () => {
        if (!ficha) return;
        const nombrePart = ficha.participanteNombres ? ficha.participanteNombres.replace(/\s+/g, '_') : 'Participante';
        const contenedor = document.getElementById('seccion-imprimible-ficha');
        if (!contenedor) return;

        const estilosDoc = `
            <style>
                body { font-family: Arial, Calibri, sans-serif; font-size: 10pt; color: #111; }
                table { border-collapse: collapse; width: 100%; margin-bottom: 8pt; }
                th, td { border: 1px solid #333; padding: 4pt 6pt; font-size: 9pt; }
                .titulo-seccion { background-color: #f0f0f0; font-weight: bold; text-align: left; padding: 4pt; font-size: 10pt; border: 1px solid #333; }
                .campo-etiqueta { font-weight: bold; font-size: 9pt; }
                .subrayado { border-bottom: 1px solid #333; min-width: 150pt; display: inline-block; }
                .caja-firma { border: 1px solid #333; height: 120pt; text-align: center; vertical-align: bottom; padding: 6pt; font-size: 8.5pt; }
                .caja-huella { border: 1px solid #333; width: 70pt; height: 80pt; margin: auto; text-align: center; }
            </style>
        `;

        const htmlCompleto = `
            <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
            <head><meta charset="utf-8"><title>Ficha de Ingreso</title>${estilosDoc}</head>
            <body>${contenedor.innerHTML}</body></html>
        `;

        const blob = new Blob(['\ufeff' + htmlCompleto], { type: 'application/msword;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Ficha_Ingreso_${nombrePart}.doc`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    if (!tienePermiso) {
        return (
            <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No cuenta con los permisos necesarios para acceder a la Ficha de Matrícula.
                    Este reporte es exclusivo para los usuarios con rol Secretaría, Administrador, Director o Monitoreo.
                </Alert>
                <Button component={Link} to="/reporte" variant="outlined" startIcon={<IconVolver />}>
                    Volver al Catálogo de Reportes
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            {/* BARRA SUPERIOR DE ACCIONES (SE OCULTA AL IMPRIMIR) */}
            <Paper className="no-print" elevation={2} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                component={Link}
                                to="/reporte"
                                variant="outlined"
                                size="small"
                                startIcon={<IconVolver />}
                            >
                                Catálogo
                            </Button>
                            {idParam > 0 && (
                                <Button
                                    component={Link}
                                    to={`/member/edit/${idParam}`}
                                    variant="text"
                                    size="small"
                                >
                                    Ver Participante
                                </Button>
                            )}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            bloqueado={bloqueado}
                            onChange={(nueva) => {
                                setIdFilial(nueva);
                                setParticipanteSeleccionado(null);
                                setFicha(null);
                                setSearchParams({});
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={8} md={4}>
                        <Autocomplete
                            size="small"
                            options={participantes}
                            getOptionLabel={(option) => `${option.participante || ''} ${option.numeroDocumento ? `(${option.numeroDocumento})` : ''}`}
                            value={participanteSeleccionado}
                            onChange={handleSelectParticipante}
                            loading={cargandoLista}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Buscar Participante"
                                    placeholder="Escriba nombres o apellidos..."
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<IconPrint />}
                                onClick={handleImprimir}
                                disabled={!ficha}
                            >
                                Imprimir
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<IconPdf />}
                                onClick={handleImprimir}
                                disabled={!ficha}
                            >
                                PDF
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<IconWord />}
                                onClick={handleExportarWord}
                                disabled={!ficha}
                            >
                                Word
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* MENSAJES DE ESTADO */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} className="no-print">
                    {error}
                </Alert>
            )}

            {cargandoFicha && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }} className="no-print">
                    <CircularProgress />
                </Box>
            )}

            {!ficha && !cargandoFicha && (
                <Card className="no-print" sx={{ p: 4, textAlign: 'center', bgcolor: '#fbfbfb' }}>
                    <IconFicha sx={{ fontSize: 60, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                        Seleccione un participante para visualizar su Ficha de Matrícula / Registro
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Puede buscar por nombre, apellidos o DNI en la barra superior.
                    </Typography>
                </Card>
            )}

            {/* FORMATO OFICIAL IMPRIMIBLE (DOCUMENTO A4) */}
            {ficha && (
                <Box
                    id="seccion-imprimible-ficha"
                    sx={{
                        bgcolor: '#fff',
                        color: '#000',
                        maxWidth: '210mm',
                        margin: '0 auto',
                        p: { xs: 2, sm: '15mm' },
                        boxShadow: { xs: 1, sm: 3 },
                        fontFamily: 'Arial, Calibri, sans-serif',
                        fontSize: '9.5pt',
                        lineHeight: 1.3,
                        '@media print': {
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            maxWidth: '100%',
                            fontSize: '9.5pt'
                        }
                    }}
                >
                    {/* ENCABEZADO INSTITUCIONAL */}
                    <Box sx={{ textAlign: 'center', mb: 2, pb: 1, borderBottom: '2px solid #333' }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '14pt', letterSpacing: 1, textTransform: 'uppercase' }}>
                            CENTRO TERAPÉUTICO KOLOB
                        </Typography>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '11pt', letterSpacing: 0.5, mt: 0.3 }}>
                            FICHA DE MATRÍCULA / REGISTRO DEL PARTICIPANTE
                        </Typography>
                        {ficha.filial && (
                            <Typography sx={{ fontSize: '9pt', color: '#444' }}>
                                FILIAL: {ficha.filial.toUpperCase()}
                            </Typography>
                        )}
                    </Box>

                    {/* LÍNEA DE TERAPEUTA Y FECHA DE INGRESO */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, fontSize: '10pt', fontWeight: 'bold' }}>
                        <Box sx={{ flex: 1, mr: 2 }}>
                            TERAPEUTA: <span style={{ fontWeight: 'normal', textDecoration: 'underline' }}>{ficha.terapeuta || '________________________________________'}</span>
                        </Box>
                        <Box sx={{ whiteSpace: 'nowrap' }}>
                            FECHA DE INGRESO: <span style={{ fontWeight: 'normal', textDecoration: 'underline' }}>{formatearFecha(ficha.fechaIngreso) || '_____/_____/________'}</span>
                        </Box>
                    </Box>

                    {/* SECCIÓN 1: DATOS PERSONALES DEL PARTICIPANTE */}
                    <Box sx={{ mb: 2, border: '1px solid #333' }}>
                        <Box sx={{ bgcolor: '#eee', p: '4px 8px', fontWeight: 'bold', borderBottom: '1px solid #333', fontSize: '10pt' }}>
                            DATOS PERSONALES DEL PARTICIPANTE
                        </Box>
                        <Box sx={{ p: 1 }}>
                            <Box sx={{ mb: 0.8 }}>
                                <strong>Nombres y apellidos completos:</strong>{' '}
                                <span style={{ textDecoration: 'underline' }}>{ficha.participanteNombres || '____________________________________________________________________'}</span>
                                {ficha.participanteDni && (
                                    <span style={{ marginLeft: 16 }}>
                                        <strong>DNI:</strong> {ficha.participanteDni}
                                    </span>
                                )}
                            </Box>
                            <Box sx={{ mb: 0.8 }}>
                                <strong>Fecha de Nacimiento:</strong>{' '}
                                <span style={{ textDecoration: 'underline' }}>{formatearFecha(ficha.fechaNacimiento) || '_____/_____/________'}</span>
                            </Box>
                            <Box sx={{ mb: 0.8 }}>
                                <strong>Domicilio:</strong>{' '}
                                <span style={{ textDecoration: 'underline' }}>{ficha.Domicilio || ficha.domicilio || '____________________________________________________________________'}</span>
                            </Box>
                            <Box sx={{ mb: 0.8 }}>
                                <strong>En caso de accidente llevarlo a:</strong>{' '}
                                <span style={{ textDecoration: 'underline' }}>{ficha.lugarCasoAccidente || '________________________________________________________________'}</span>
                            </Box>
                            <Box sx={{ mb: 0.8, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                                <Box sx={{ mr: 3 }}>
                                    <strong>Cuenta con algún tipo de seguro:</strong>{' '}
                                    SI ( {ficha.tieneSeguro === true ? 'X' : '  '} ) &nbsp;&nbsp; NO ( {ficha.tieneSeguro === false ? 'X' : '  '} )
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <strong>Empresa o institución:</strong>{' '}
                                    <span style={{ textDecoration: 'underline' }}>{ficha.empresaSeguro || '_________________________________'}</span>
                                </Box>
                            </Box>
                            <Box sx={{ mb: 0.8 }}>
                                <strong>Alérgico a algún medicamento:</strong>{' '}
                                <span style={{ textDecoration: 'underline' }}>{ficha.alergias || 'NINGUNO'}</span>
                            </Box>
                            <Box sx={{ mb: 0.8 }}>
                                <strong>¿Tiene hermanos? Indicar edades:</strong>{' '}
                                <span style={{ textDecoration: 'underline' }}>
                                    {ficha.tieneHermanos ? (ficha.detalleHermanos || 'SI') : 'NO'}
                                </span>
                            </Box>
                            <Box>
                                <strong>Tiene evaluación diagnóstica:</strong>{' '}
                                SI ( {ficha.tieneDiagnostico === true ? 'X' : '  '} ) &nbsp;&nbsp; NO ( {ficha.tieneDiagnostico === false ? 'X' : '  '} )
                            </Box>
                        </Box>
                    </Box>

                    {/* SECCIÓN 2: DATOS DE LOS PADRES */}
                    <Box sx={{ mb: 2, border: '1px solid #333' }}>
                        <Box sx={{ bgcolor: '#eee', p: '4px 8px', fontWeight: 'bold', borderBottom: '1px solid #333', fontSize: '10pt' }}>
                            DATOS DE LOS PADRES
                        </Box>
                        <Grid container sx={{ '& > div': { p: 1 } }}>
                            {/* COLUMNA MAMÁ */}
                            <Grid item xs={6} sx={{ borderRight: '1px solid #333' }}>
                                <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 1, fontSize: '9.5pt' }}>
                                    MAMÁ
                                </Typography>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Nombres y apellidos:</strong><br />
                                    <span>{ficha.madreNombres || '__________________________________'}</span>
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>DNI:</strong> {ficha.madreDni || '____________________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Fecha de Nacimiento:</strong> {formatearFecha(ficha.madreFechaNacimiento) || '_____/_____/________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Profesión u ocupación:</strong><br />
                                    <span>{ficha.madreOcupacion || '__________________________________'}</span>
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Teléfono de Casa:</strong> {ficha.madreTelefonoCasa || '____________________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Lugar de Trabajo:</strong><br />
                                    <span>{ficha.madreLugarTrabajo || '__________________________________'}</span>
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Teléfono de trabajo:</strong> {ficha.madreTelefonoTrabajo || '____________________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Celular:</strong> {ficha.madreCelular || '____________________'}
                                </Box>
                                <Box>
                                    <strong>Correo Electrónico:</strong><br />
                                    <span>{ficha.madreEmail || '__________________________________'}</span>
                                </Box>
                            </Grid>

                            {/* COLUMNA PAPÁ */}
                            <Grid item xs={6}>
                                <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 1, fontSize: '9.5pt' }}>
                                    PAPÁ
                                </Typography>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Nombres y apellidos:</strong><br />
                                    <span>{ficha.padreNombres || '__________________________________'}</span>
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>DNI:</strong> {ficha.padreDni || '____________________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Fecha de Nacimiento:</strong> {formatearFecha(ficha.padreFechaNacimiento) || '_____/_____/________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Profesión u ocupación:</strong><br />
                                    <span>{ficha.padreOcupacion || '__________________________________'}</span>
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Teléfono de Casa:</strong> {ficha.padreTelefonoCasa || '____________________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Lugar de Trabajo:</strong><br />
                                    <span>{ficha.padreLugarTrabajo || '__________________________________'}</span>
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Teléfono de trabajo:</strong> {ficha.padreTelefonoTrabajo || '____________________'}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                    <strong>Celular:</strong> {ficha.padreCelular || '____________________'}
                                </Box>
                                <Box>
                                    <strong>Correo Electrónico:</strong><br />
                                    <span>{ficha.padreEmail || '__________________________________'}</span>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* SECCIÓN 3: PERSONAS AUTORIZADAS PARA RECOGER AL NIÑO */}
                    <Box sx={{ mb: 2, border: '1px solid #333' }}>
                        <Box sx={{ bgcolor: '#eee', p: '4px 8px', fontWeight: 'bold', borderBottom: '1px solid #333', fontSize: '10pt' }}>
                            PERSONAS AUTORIZADAS PARA RECOGER AL NIÑO
                        </Box>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#fafafa' }}>
                                    <th style={{ border: '1px solid #333', padding: '5px 8px', width: '50%' }}>
                                        NOMBRES Y APELLIDOS
                                    </th>
                                    <th style={{ border: '1px solid #333', padding: '5px 8px', width: '25%' }}>
                                        D.N.I.
                                    </th>
                                    <th style={{ border: '1px solid #333', padding: '5px 8px', width: '25%' }}>
                                        TELÉFONO
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {ficha.personasAutorizadas && ficha.personasAutorizadas.length > 0 ? (
                                    <>
                                        {ficha.personasAutorizadas.map((pa, idx) => (
                                            <tr key={idx}>
                                                <td style={{ border: '1px solid #333', padding: '5px 8px' }}>
                                                    {pa.nombres || ''}
                                                </td>
                                                <td style={{ border: '1px solid #333', padding: '5px 8px' }}>
                                                    {pa.dni || ''}
                                                </td>
                                                <td style={{ border: '1px solid #333', padding: '5px 8px' }}>
                                                    {pa.telefono || ''}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Rellenar hasta al menos 2 filas si solo hay 1 registrada */}
                                        {ficha.personasAutorizadas.length < 2 && (
                                            <tr>
                                                <td style={{ border: '1px solid #333', padding: '10px 8px' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid #333', padding: '10px 8px' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid #333', padding: '10px 8px' }}>&nbsp;</td>
                                            </tr>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <tr>
                                            <td style={{ border: '1px solid #333', padding: '12px 8px' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid #333', padding: '12px 8px' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid #333', padding: '12px 8px' }}>&nbsp;</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid #333', padding: '12px 8px' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid #333', padding: '12px 8px' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid #333', padding: '12px 8px' }}>&nbsp;</td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </Box>

                    {/* SECCIÓN 4: FIRMAS Y HUELLAS */}
                    <Grid container spacing={2} sx={{ mt: 1, pageBreakInside: 'avoid' }}>
                        {/* PERSONA QUE AUTORIZA */}
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    border: '1px solid #333',
                                    p: 1.5,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Typography sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1, fontSize: '9.5pt' }}>
                                    PERSONA QUE AUTORIZA
                                </Typography>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={7}>
                                        <Box sx={{ mt: 3, pt: 1, borderTop: '1px solid #333', textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '8.5pt' }}>FIRMA</Typography>
                                        </Box>
                                        <Box sx={{ mt: 1, fontSize: '8.5pt' }}>
                                            <strong>Nombre:</strong> {ficha.personaAutorizaNombres || '___________________'}
                                        </Box>
                                        <Box sx={{ mt: 0.5, fontSize: '8.5pt' }}>
                                            <strong>DNI:</strong> {ficha.personaAutorizaDni || '___________________'}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={5} sx={{ textAlign: 'center' }}>
                                        <Box
                                            sx={{
                                                border: '1px solid #666',
                                                height: 80,
                                                width: 65,
                                                margin: '0 auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '7.5pt',
                                                color: '#666'
                                            }}
                                        >
                                            HUELLA
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {/* TERAPEUTA A CARGO */}
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    border: '1px solid #333',
                                    p: 1.5,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Typography sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1, fontSize: '9.5pt' }}>
                                    TERAPEUTA A CARGO
                                </Typography>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={7}>
                                        <Box sx={{ mt: 3, pt: 1, borderTop: '1px solid #333', textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '8.5pt' }}>FIRMA</Typography>
                                        </Box>
                                        <Box sx={{ mt: 1, fontSize: '8.5pt' }}>
                                            <strong>Nombre:</strong> {ficha.terapeuta || '___________________'}
                                        </Box>
                                        <Box sx={{ mt: 0.5, fontSize: '8.5pt' }}>
                                            <strong>DNI:</strong> {ficha.terapeutaDni || '___________________'}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={5} sx={{ textAlign: 'center' }}>
                                        <Box
                                            sx={{
                                                border: '1px solid #666',
                                                height: 80,
                                                width: 65,
                                                margin: '0 auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '7.5pt',
                                                color: '#666'
                                            }}
                                        >
                                            HUELLA
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* ESTILOS GLOBALES DE IMPRESIÓN */}
            <style>
                {`
                @media print {
                    nav, header, aside, .no-print, .MuiAppBar-root, .MuiDrawer-root, #root > div > header {
                        display: none !important;
                    }
                    body, html {
                        background: #fff !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    #seccion-imprimible-ficha {
                        box-shadow: none !important;
                        margin: 0 !important;
                        width: 100% !important;
                        max-width: 100% !important;
                        padding: 0 !important;
                    }
                    @page {
                        size: A4 portrait;
                        margin: 12mm;
                    }
                }
                `}
            </style>
        </Box>
    );
};

export default FichaParticipante;

